import React, { Fragment, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { IStepNavigation } from './StepNavigation';
import { MDFIcon } from '../MDFIcon';
import { colorValue } from '../../util/colorStyles';

export interface INavStep extends Omit<IStepNavigation, 'sequential'> {
  // To set the navigation step state to active. StepNavigation component sets this property to 'true' in case of non-sequencial navigation.
  active?: boolean;

  // To set the navigation step state to complete.
  complete?: boolean;

  // The class name of the icon to be displayed in the step.
  icon?: string;

  // Id for each step.
  id?: string;

  // Specifies lastChild or not. StepNavigation component sets this property.
  lastChild?: boolean;

  // Specifies step position in container. StepNavigation component sets this property.
  position?: number;
}

type NavStepDirection = 'forwards' | 'backwards';

export const NavStep = ({
  active = false,
  activeAccent = 'accent-0-dark',
  complete = false,
  incompleteAccent = 'neutral-dark',
  lastChild = false,
  stepNavType = 'numerical',
  ...props
}: INavStep) => {
  const { icon, id, position, showLines } = props;
  const [direction, setDirection] = useState<NavStepDirection>((complete === true) ? 'forwards' : 'backwards');
  const [isComplete, setIsComplete] = useState(complete);

  useEffect(() => {
    if (complete !== isComplete) {
      setDirection(complete === true ? 'forwards' : 'backwards');
      setIsComplete(complete);
    }
  }, [complete]);

  const getStylesForElementAccent = (activeAccentColor: string, incompleteAccentColor: string) => {
    const activeAccentColorValue = colorValue(activeAccentColor);
    const incompleteAccentColorValue = colorValue(incompleteAccentColor);

    const activeIndicator = {
      color: activeAccentColorValue,
      borderColor: activeAccentColorValue
    };

    const inactiveIndicator = {
      color: incompleteAccentColorValue,
      borderColor: incompleteAccentColorValue
    };

    const activeLine = {
      borderColor: activeAccentColorValue
    };

    const inactiveLine = {
      borderColor: incompleteAccentColorValue
    };

    return { activeIndicator, inactiveIndicator, activeLine, inactiveLine };
  };

  const { activeIndicator, inactiveIndicator, activeLine, inactiveLine } = useMemo(() => {
    return getStylesForElementAccent(activeAccent, incompleteAccent);
  }, [activeAccent, incompleteAccent]);

  const stepClasses = {
    'step-navigation__step': true,
    active: active,
    complete: isComplete,
    [direction]: showLines
  };

  return (
    <div id={id} key={position} className="h-full relative flex flex-row flex-no-wrap items-stretch step-navigation">
      <div className={classNames(stepClasses)}>
        <div style={active ? activeIndicator : inactiveIndicator} className="border-solid text-center step-navigation__step-indicator mdf-circle-icon--xs">
          { stepNavType === 'numerical' && !icon && !isComplete ? <span className="step-navigation__step-indicator-number">
            { position }
          </span> : null }
          { isComplete ? <MDFIcon className="fa fa-check"/> : null}
          { !isComplete && icon ? <MDFIcon className={'block text-center step-icon ' + icon}/> : null }
        </div>

        { !lastChild && showLines ? <Fragment>
          <div style={inactiveLine} className="absolute border-l-2 border-r-0 border-dashed bottom-0 step-navigation__step-line"></div>
          <div style={activeLine} className="absolute border-solid border-r-0 bottom-0 step-navigation__step-line active"></div>
        </Fragment> : null}
      </div>
      <div className="flex-auto">
        {props.children}
      </div>
    </div>
  );
};

NavStep.displayName = 'NavStep';
